export function initStickyHeader(): void {
  const header = document.getElementById('site-header')
  if (!header) return

  let lastScrollY = 0
  let ticking = false
  const deadZone = 10
  const hideThreshold = 100

  const update = (): void => {
    const currentScrollY = window.scrollY

    // Add/remove scrolled state for backdrop blur
    if (currentScrollY > 10) {
      header.classList.add('header-scrolled')
    } else {
      header.classList.remove('header-scrolled')
    }

    // Don't hide/show when mobile nav is open
    if (document.body.classList.contains('nav-open')) {
      lastScrollY = currentScrollY
      ticking = false
      return
    }

    const delta = currentScrollY - lastScrollY

    // Dead zone — ignore small scroll movements
    if (Math.abs(delta) < deadZone) {
      ticking = false
      return
    }

    // Scrolling down past threshold — hide
    if (delta > 0 && currentScrollY > hideThreshold) {
      header.classList.add('header-hidden')
    }

    // Scrolling up — show
    if (delta < 0) {
      header.classList.remove('header-hidden')
    }

    lastScrollY = currentScrollY
    ticking = false
  }

  window.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        window.requestAnimationFrame(update)
        ticking = true
      }
    },
    { passive: true }
  )
}

export function initMobileNav(): void {
  const hamburger = document.getElementById('hamburger')
  const nav = document.getElementById('mobile-nav')

  if (!hamburger || !nav) {
    return
  }

  const hamburgerParent = hamburger.closest('body > *')

  let isOpen = false

  function openNav(): void {
    isOpen = true
    hamburger!.classList.add('is-active')
    nav!.classList.add('is-open')
    nav!.setAttribute('aria-hidden', 'false')
    hamburger!.setAttribute('aria-expanded', 'true')
    hamburger!.setAttribute('aria-label', 'Close menu')
    document.body.classList.add('nav-open')
    document.body.style.overflow = 'hidden'

    // Make rest of page inert to prevent focus escaping
    document.querySelectorAll('body > *:not(#mobile-nav)').forEach((el) => {
      if (el instanceof HTMLElement && el !== hamburgerParent) {
        el.setAttribute('inert', '')
        el.setAttribute('aria-hidden', 'true')
      }
    })

    // Focus the first nav link
    const firstLink = nav!.querySelector<HTMLAnchorElement>('.mobile-nav-link')
    firstLink?.focus()
  }

  function closeNav(): void {
    isOpen = false
    hamburger!.classList.remove('is-active')
    nav!.classList.remove('is-open')
    nav!.setAttribute('aria-hidden', 'true')
    hamburger!.setAttribute('aria-expanded', 'false')
    hamburger!.setAttribute('aria-label', 'Open menu')
    document.body.classList.remove('nav-open')
    document.body.style.overflow = ''

    // Remove inert from rest of page
    document.querySelectorAll('body > *:not(#mobile-nav)').forEach((el) => {
      if (el instanceof HTMLElement && el !== hamburgerParent) {
        el.removeAttribute('inert')
        el.removeAttribute('aria-hidden')
      }
    })

    // Return focus to the hamburger button
    hamburger!.focus()
  }

  const toggleNav = () => (isOpen ? closeNav() : openNav())

  // Toggle on hamburger click
  hamburger.addEventListener('click', toggleNav)

  // Close on Escape key
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) {
      closeNav()
    }
  })
}
